// â”€â”€â”€ Contact edit form (Phase 3) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Reusable form used by both:
//   - CreateContactModal (mode='create')
//   - ContactSlideOver edit mode (mode='edit')
//
// Validates email/phone before submit. Reports field-level errors inline.

import React, { useState, useEffect } from 'react';
import type { Contact, UserType, EmploymentType } from '../types';
import {
  USER_TYPE_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  GRADE_LABELS,
  DELIVERY_PREFERENCE_LABELS,
} from '../types';
import { isValidEmail, isSyntheticEmail, generateSyntheticEmail } from '../lib/email';
import { isValidPhone, formatPhoneDisplay } from '../lib/phone';
import type { ContactCreateInput, ContactUpdates } from '../services/contactsService';

export type FormMode = 'create' | 'edit';

interface Props {
  mode: FormMode;
  initial?: Contact;
  submitLabel?: string;
  onSubmit: (data: ContactCreateInput | ContactUpdates) => Promise<void>;
  onCancel?: () => void;
}

const Field: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, required, error, hint, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-stone-700 flex items-center gap-1">
      {label}
      {required && <span className="text-red-500">*</span>}
    </span>
    <div className="mt-1">{children}</div>
    {hint && !error && <p className="text-[11px] text-stone-400 mt-1">{hint}</p>}
    {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
  </label>
);

const ContactEditForm: React.FC<Props> = ({
  mode,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}) => {
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [email, setEmail]             = useState(initial?.email ?? '');
  const [phone, setPhone]             = useState(initial?.phone ?? '');
  const [employeeId, setEmployeeId]   = useState(initial?.employee_id ?? '');
  const [userType, setUserType]       = useState<UserType | ''>(initial?.user_type ?? '');
  const [employmentType, setEmploymentType] = useState<EmploymentType | ''>(initial?.employment_type ?? '');
  const [jobTitle, setJobTitle]       = useState(initial?.jobTitle ?? '');
  const [department, setDepartment]   = useState(initial?.department ?? '');
  const [grade, setGrade]             = useState<string>(initial?.grade ?? '');
  const [site, setSite]               = useState(initial?.site ?? '');
  const [startDate, setStartDate]     = useState(initial?.startDate ?? '');
  const [unitId, setUnitId]           = useState(initial?.unitId ?? '');
  const [unitName, setUnitName]       = useState(initial?.unitName ?? '');
  const [directManagerId, setDirectManagerId] = useState(initial?.directManagerId ?? '');
  const [isLeader, setIsLeader]       = useState<boolean>(initial?.isLeader ?? false);
  const [deliveryPref, setDeliveryPref] = useState<Contact['delivery_preference'] | ''>(
    initial?.delivery_preference ?? '',
  );

  const [useSynthetic, setUseSynthetic] = useState<boolean>(
    initial ? isSyntheticEmail(initial.email) : false,
  );

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  // Auto-generate synthetic email when toggle ON + employee_id present
  useEffect(() => {
    if (useSynthetic && employeeId && mode === 'create') {
      try {
        setEmail(generateSyntheticEmail(employeeId));
      } catch {
        // ignore â€” user will see required field error
      }
    }
  }, [useSynthetic, employeeId, mode]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!displayName.trim()) e.displayName = 'Required';
    if (!email.trim()) {
      e.email = 'Required';
    } else if (!isValidEmail(email)) {
      e.email = 'Invalid email format';
    }
    if (phone && !isValidPhone(phone)) {
      e.phone = 'Invalid Indonesian phone (e.g. 0812-3456-7890)';
    }
    if (useSynthetic && !phone) {
      e.phone = 'Phone is required when using synthetic email';
    }
    if (deliveryPref === 'whatsapp' && !phone) {
      e.phone = 'Phone is required for WhatsApp delivery';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setServerError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload: any = {
        displayName: displayName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        user_type: userType || undefined,
        employee_id: employeeId.trim() || undefined,
        employment_type: employmentType || undefined,
        jobTitle: jobTitle.trim() || undefined,
        department: department.trim() || undefined,
        grade: grade || undefined,
        site: site.trim() || undefined,
        startDate: startDate || undefined,
        unitId: unitId.trim() || undefined,
        unitName: unitName.trim() || undefined,
        directManagerId: directManagerId.trim() || undefined,
        isLeader,
        delivery_preference: deliveryPref || undefined,
      };
      if (mode === 'create') payload.source = 'manual';

      await onSubmit(payload);
    } catch (e: any) {
      setServerError(e?.message ?? String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Identity */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-bold text-stone-400 uppercase tracking-wider">
          Identity
        </legend>

        <Field label="Display Name" required error={errors.displayName}>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20 focus:border-[#011132]"
            placeholder="Andi Wijaya"
          />
        </Field>

        {mode === 'create' && (
          <label className="flex items-center gap-2 text-xs text-stone-700">
            <input
              type="checkbox"
              checked={useSynthetic}
              onChange={(e) => setUseSynthetic(e.target.checked)}
              className="rounded"
            />
            Use synthetic email (field staff without company email)
          </label>
        )}

        <Field
          label="Email"
          required
          error={errors.email}
          hint={
            useSynthetic
              ? 'Auto-generated from employee_id, e.g. rr-2025-047@field.rascal.local'
              : 'Lowercased on save'
          }
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={useSynthetic && mode === 'create'}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20 focus:border-[#011132] disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="andi@rascalrepublic.com"
          />
        </Field>

        <Field
          label="Phone"
          error={errors.phone}
          hint={
            phone && isValidPhone(phone)
              ? `Normalized: ${formatPhoneDisplay(phone)}`
              : 'Indonesia format: 0812-3456-7890 or +6281234567890'
          }
        >
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20 focus:border-[#011132]"
            placeholder="0812-3456-7890"
          />
        </Field>

        <Field label="Employee ID" hint="HRIS link (e.g. RR-2025-047)">
          <input
            type="text"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20 focus:border-[#011132]"
            placeholder="RR-2025-047"
          />
        </Field>
      </fieldset>

      {/* Employment */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-bold text-stone-400 uppercase tracking-wider">
          Employment
        </legend>

        <div className="grid grid-cols-2 gap-3">
          <Field label="User Type">
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value as UserType | '')}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">â€”</option>
              {Object.entries(USER_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>

          <Field label="Employment Type">
            <select
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value as EmploymentType | '')}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">â€”</option>
              {Object.entries(EMPLOYMENT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Job Title">
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
            placeholder="Operations Supervisor"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Department">
            <input
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Operations"
            />
          </Field>

          <Field label="Grade">
            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">â€”</option>
              {Object.entries(GRADE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Site">
            <input
              type="text"
              value={site}
              onChange={(e) => setSite(e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Lombok HQ"
            />
          </Field>
          <Field label="Start Date">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        </div>
      </fieldset>

      {/* Org */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-bold text-stone-400 uppercase tracking-wider">
          Org Structure
        </legend>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Unit ID">
            <input
              type="text"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Unit Name">
            <input
              type="text"
              value={unitName}
              onChange={(e) => setUnitName(e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        </div>

        <Field label="Direct Manager ID (contactId)">
          <input
            type="text"
            value={directManagerId}
            onChange={(e) => setDirectManagerId(e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
            placeholder="contactId of manager"
          />
        </Field>

        <label className="flex items-center gap-2 text-xs text-stone-700">
          <input
            type="checkbox"
            checked={isLeader}
            onChange={(e) => setIsLeader(e.target.checked)}
            className="rounded"
          />
          Is a leader (gates OKR dept-lead features + EPR HR framework)
        </label>
      </fieldset>

      {/* Delivery */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-bold text-stone-400 uppercase tracking-wider">
          Delivery Preference
        </legend>
        <Field
          label="HR-initiated channel"
          hint="Login OTP is always SMS regardless of this preference."
        >
          <select
            value={deliveryPref}
            onChange={(e) => setDeliveryPref(e.target.value as Contact['delivery_preference'] | '')}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">â€”</option>
            {Object.entries(DELIVERY_PREFERENCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </Field>
      </fieldset>

      {/* Server error */}
      {serverError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {serverError}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-3 border-t border-stone-100">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-lg border border-stone-200"
            disabled={submitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="px-5 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
          style={{ background: '#011132' }}
        >
          {submitting ? 'Savingâ€¦' : submitLabel ?? (mode === 'create' ? 'Create' : 'Save')}
        </button>
      </div>
    </form>
  );
};

export default ContactEditForm;
